import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface Question {
  id: string
  subject: string
  topic: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  options: string[]
  correctAnswer: number
  explanation: string
  timeLimit?: number
}

interface ExamResult {
  id: string
  examId: string
  examTitle: string
  date: string
  duration: number
  totalQuestions: number
  correctAnswers: number
  wrongAnswers: number
  emptyAnswers: number
  score: number
  percentage: number
  subjectScores: Record<string, { correct: number; total: number; percentage: number }>
  answers: Record<string, number | null>
  timeSpent: Record<string, number>
}

interface Exam {
  id: string
  title: string
  description: string
  subject: string
  duration: number
  questions: Question[]
  isActive: boolean
  createdAt: string
  difficulty: 'easy' | 'medium' | 'hard' | 'mixed'
  tags: string[]
}

interface ExamState {
  exams: Exam[]
  results: ExamResult[]
  currentExam: Exam | null
  currentQuestion: number
  answers: Record<string, number | null>
  timeRemaining: number
  isExamActive: boolean
  examStartTime: number | null
  questionStartTime: number | null
  questionBank: Question[]
  filters: {
    subject: string
    difficulty: string
    topic: string
  }
  stats: {
    totalExams: number
    averageScore: number
    bestScore: number
    totalQuestions: number
    correctAnswers: number
    accuracy: number
    averageTime: number
  }
}

const sampleQuestions: Question[] = [
  {
    id: '1',
    subject: 'Matematik',
    topic: 'Cebirsel İfadeler',
    difficulty: 'medium',
    question: '3x + 5 = 14 denkleminde x kaçtır?',
    options: ['2', '3', '4', '5'],
    correctAnswer: 1,
    explanation: '3x + 5 = 14 → 3x = 9 → x = 3',
    timeLimit: 120
  },
  {
    id: '2',
    subject: 'Türkçe',
    topic: 'Okuduğunu Anlama',
    difficulty: 'easy',
    question: 'Aşağıdaki cümlelerden hangisi kurallı yazılmıştır?',
    options: [
      'Kitabı masanın üzerine koydum.',
      'Kitabı masanın üstüne koydum.',
      'Kitabı masanın üzerinde koydum.',
      'Kitabı masanın üstünde koydum.'
    ],
    correctAnswer: 0,
    explanation: '"Üzerine" doğru kullanımdır.',
    timeLimit: 90
  },
  {
    id: '3',
    subject: 'Fen Bilimleri',
    topic: 'Kuvvet ve Hareket',
    difficulty: 'hard',
    question: 'Sürtünmesiz yatay düzlemde 10 N kuvvetle itilen 2 kg kütleli cismin ivmesi kaç m/s² dir?',
    options: ['2', '5', '10', '20'],
    correctAnswer: 1,
    explanation: 'F = m × a → 10 = 2 × a → a = 5 m/s²',
    timeLimit: 150
  }
]

const initialState: ExamState = {
  exams: [
    {
      id: '1',
      title: 'Matematik Deneme Sınavı 1',
      description: 'Cebirsel ifadeler ve denklemler konularını kapsayan deneme sınavı',
      subject: 'Matematik',
      duration: 3600,
      questions: sampleQuestions.filter(q => q.subject === 'Matematik'),
      isActive: true,
      createdAt: '2024-01-10',
      difficulty: 'mixed',
      tags: ['deneme', 'matematik', 'cebir']
    },
    {
      id: '2',
      title: 'Genel Deneme Sınavı',
      description: 'Tüm dersleri kapsayan kapsamlı deneme sınavı',
      subject: 'Genel',
      duration: 7200,
      questions: sampleQuestions,
      isActive: true,
      createdAt: '2024-01-12',
      difficulty: 'mixed',
      tags: ['deneme', 'genel', 'kapsamlı']
    }
  ],
  results: [
    {
      id: '1',
      examId: '1',
      examTitle: 'Matematik Deneme Sınavı 1',
      date: '2024-01-14',
      duration: 3400,
      totalQuestions: 20,
      correctAnswers: 16,
      wrongAnswers: 3,
      emptyAnswers: 1,
      score: 320,
      percentage: 80,
      subjectScores: {
        'Matematik': { correct: 16, total: 20, percentage: 80 }
      },
      answers: {},
      timeSpent: {}
    },
    {
      id: '2',
      examId: '2',
      examTitle: 'Genel Deneme Sınavı',
      date: '2024-01-13',
      duration: 6800,
      totalQuestions: 100,
      correctAnswers: 75,
      wrongAnswers: 20,
      emptyAnswers: 5,
      score: 375,
      percentage: 75,
      subjectScores: {
        'Matematik': { correct: 15, total: 20, percentage: 75 },
        'Türkçe': { correct: 16, total: 20, percentage: 80 },
        'Fen Bilimleri': { correct: 14, total: 20, percentage: 70 },
        'Sosyal Bilgiler': { correct: 15, total: 20, percentage: 75 },
        'İngilizce': { correct: 15, total: 20, percentage: 75 }
      },
      answers: {},
      timeSpent: {}
    }
  ],
  currentExam: null,
  currentQuestion: 0,
  answers: {},
  timeRemaining: 0,
  isExamActive: false,
  examStartTime: null,
  questionStartTime: null,
  questionBank: sampleQuestions,
  filters: {
    subject: '',
    difficulty: '',
    topic: ''
  },
  stats: {
    totalExams: 2,
    averageScore: 77.5,
    bestScore: 80,
    totalQuestions: 120,
    correctAnswers: 91,
    accuracy: 75.8,
    averageTime: 5100
  }
}

const examSlice = createSlice({
  name: 'exam',
  initialState,
  reducers: {
    startExam: (state, action: PayloadAction<string>) => {
      const exam = state.exams.find(e => e.id === action.payload)
      if (exam) {
        state.currentExam = exam
        state.currentQuestion = 0
        state.answers = {}
        state.timeRemaining = exam.duration
        state.isExamActive = true
        state.examStartTime = Date.now()
        state.questionStartTime = Date.now()
      }
    },
    endExam: (state) => {
      if (state.currentExam && state.examStartTime) {
        const duration = Date.now() - state.examStartTime
        const result = calculateExamResult(state.currentExam, state.answers, duration)
        state.results.unshift(result)
        
        // Update stats
        state.stats.totalExams += 1
        state.stats.totalQuestions += state.currentExam.questions.length
        state.stats.correctAnswers += result.correctAnswers
        state.stats.accuracy = (state.stats.correctAnswers / state.stats.totalQuestions) * 100
        state.stats.averageScore = state.results.reduce((sum, r) => sum + r.percentage, 0) / state.results.length
        state.stats.bestScore = Math.max(state.stats.bestScore, result.percentage)
        state.stats.averageTime = state.results.reduce((sum, r) => sum + r.duration, 0) / state.results.length
      }
      
      state.currentExam = null
      state.currentQuestion = 0
      state.answers = {}
      state.timeRemaining = 0
      state.isExamActive = false
      state.examStartTime = null
      state.questionStartTime = null
    },
    pauseExam: (state) => {
      state.isExamActive = false
    },
    resumeExam: (state) => {
      state.isExamActive = true
      state.questionStartTime = Date.now()
    },
    answerQuestion: (state, action: PayloadAction<{ questionId: string; answer: number }>) => {
      state.answers[action.payload.questionId] = action.payload.answer
    },
    nextQuestion: (state) => {
      if (state.currentExam && state.currentQuestion < state.currentExam.questions.length - 1) {
        state.currentQuestion += 1
        state.questionStartTime = Date.now()
      }
    },
    previousQuestion: (state) => {
      if (state.currentQuestion > 0) {
        state.currentQuestion -= 1
        state.questionStartTime = Date.now()
      }
    },
    goToQuestion: (state, action: PayloadAction<number>) => {
      if (state.currentExam && action.payload >= 0 && action.payload < state.currentExam.questions.length) {
        state.currentQuestion = action.payload
        state.questionStartTime = Date.now()
      }
    },
    updateTimeRemaining: (state, action: PayloadAction<number>) => {
      state.timeRemaining = Math.max(0, action.payload)
      if (state.timeRemaining === 0) {
        // Auto-end exam when time runs out
        examSlice.caseReducers.endExam(state)
      }
    },
    addExam: (state, action: PayloadAction<Omit<Exam, 'id' | 'createdAt'>>) => {
      const newExam: Exam = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      }
      state.exams.push(newExam)
    },
    updateExam: (state, action: PayloadAction<Exam>) => {
      const index = state.exams.findIndex(exam => exam.id === action.payload.id)
      if (index !== -1) {
        state.exams[index] = action.payload
      }
    },
    deleteExam: (state, action: PayloadAction<string>) => {
      state.exams = state.exams.filter(exam => exam.id !== action.payload)
    },
    addQuestion: (state, action: PayloadAction<Omit<Question, 'id'>>) => {
      const newQuestion: Question = {
        ...action.payload,
        id: Date.now().toString()
      }
      state.questionBank.push(newQuestion)
    },
    updateQuestion: (state, action: PayloadAction<Question>) => {
      const index = state.questionBank.findIndex(q => q.id === action.payload.id)
      if (index !== -1) {
        state.questionBank[index] = action.payload
      }
    },
    deleteQuestion: (state, action: PayloadAction<string>) => {
      state.questionBank = state.questionBank.filter(q => q.id !== action.payload)
    },
    setFilters: (state, action: PayloadAction<{ subject?: string; difficulty?: string; topic?: string }>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = { subject: '', difficulty: '', topic: '' }
    },
    deleteExamResult: (state, action: PayloadAction<string>) => {
      state.results = state.results.filter(result => result.id !== action.payload)
    }
  },
})

function calculateExamResult(exam: Exam, answers: Record<string, number | null>, duration: number): ExamResult {
  let correctAnswers = 0
  let wrongAnswers = 0
  let emptyAnswers = 0
  const subjectScores: Record<string, { correct: number; total: number; percentage: number }> = {}
  const timeSpent: Record<string, number> = {}

  exam.questions.forEach((question, index) => {
    const answer = answers[question.id]
    const subject = question.subject

    if (!subjectScores[subject]) {
      subjectScores[subject] = { correct: 0, total: 0, percentage: 0 }
    }
    subjectScores[subject].total += 1

    if (answer === null || answer === undefined) {
      emptyAnswers += 1
    } else if (answer === question.correctAnswer) {
      correctAnswers += 1
      subjectScores[subject].correct += 1
    } else {
      wrongAnswers += 1
    }

    timeSpent[question.id] = Math.floor(Math.random() * 120) + 30 // Mock time spent
  })

  // Calculate percentages for each subject
  Object.keys(subjectScores).forEach(subject => {
    const subjectData = subjectScores[subject]
    subjectData.percentage = Math.round((subjectData.correct / subjectData.total) * 100)
  })

  const totalQuestions = exam.questions.length
  const score = correctAnswers * 5 // 5 points per correct answer
  const percentage = Math.round((correctAnswers / totalQuestions) * 100)

  return {
    id: Date.now().toString(),
    examId: exam.id,
    examTitle: exam.title,
    date: new Date().toISOString().split('T')[0],
    duration,
    totalQuestions,
    correctAnswers,
    wrongAnswers,
    emptyAnswers,
    score,
    percentage,
    subjectScores,
    answers,
    timeSpent
  }
}

export const {
  startExam,
  endExam,
  pauseExam,
  resumeExam,
  answerQuestion,
  nextQuestion,
  previousQuestion,
  goToQuestion,
  updateTimeRemaining,
  addExam,
  updateExam,
  deleteExam,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  setFilters,
  clearFilters,
  deleteExamResult,
} = examSlice.actions

export default examSlice.reducer